import {
  type Instant,
  type LocalDate,
  type Money,
  formatLocalDate,
  fromApiMoney,
  parseInstant,
  parseLocalDate,
  toApiMoney,
} from '@taler/domain'
import type { Schemas } from '../contract/types.ts'
import type { BinaryResponse, TalerClient } from '../http/client.ts'
import { forgetOnFinalClientError } from '../http/idempotency.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'

export type FuelType = 'DIESEL' | 'PETROL' | 'ELECTRIC' | 'HYBRID' | 'GAS'
export type CarStatus = 'ACTIVE' | 'SOLD'

export interface Car {
  readonly id: string
  readonly name: string
  readonly make: string | undefined
  readonly model: string | undefined
  readonly year: number | undefined
  readonly licensePlate: string | undefined
  readonly vin: string | undefined
  readonly color: string | undefined
  readonly fuelType: FuelType
  readonly initialMileageKm: number
  readonly purchaseDate: LocalDate | undefined
  readonly notes: string | undefined
  /** Derived server-side from having a SELL event; there is no status to set directly. */
  readonly status: CarStatus
  readonly isPrimary: boolean
  readonly hasPhoto: boolean
  readonly createdAt: Instant
  readonly updatedAt: Instant
}

export interface CreateCarInput {
  readonly name: string
  readonly fuelType: FuelType
  readonly initialMileageKm: number
  readonly isPrimary: boolean
  readonly make?: string
  readonly model?: string
  readonly year?: number
  readonly licensePlate?: string
  readonly vin?: string
  readonly color?: string
  readonly purchaseDate?: LocalDate
  readonly notes?: string
}

export interface UpdateCarInput {
  readonly name?: string
  readonly fuelType?: FuelType
  readonly initialMileageKm?: number
  readonly make?: string
  readonly model?: string
  readonly year?: number
  readonly licensePlate?: string
  readonly vin?: string
  readonly color?: string
  readonly purchaseDate?: LocalDate
  readonly notes?: string
}

export const CAR_EVENT_TYPES = [
  'FUEL',
  'REPAIR',
  'INSURANCE_PAYMENT',
  'TAX',
  'INSPECTION',
  'REGISTRATION',
  'CAR_WASH',
  'PARKING',
  'FINE',
  'MILEAGE_LOG',
  'SELL',
  'BUY',
  'TIRE_CHANGE',
] as const
export type CarEventType = (typeof CAR_EVENT_TYPES)[number]

export type FineType = 'SPEEDING' | 'PARKING' | 'OTHER'
export type FineStatus = 'UNPAID' | 'PAID' | 'CONTESTED'
export type InspectionResult = 'PASS' | 'FAIL' | 'CONDITIONAL'
export type WashType = 'SELF_SERVICE' | 'AUTOMATIC' | 'HAND_WASH' | 'DETAILING'
export type TireType = 'SUMMER' | 'WINTER' | 'ALL_SEASON'
export type Axle = 'FRONT' | 'REAR' | 'ALL'

/**
 * Each variant carries only its own type's fields (AGENTS.md garage note) - the wire shape
 * (`CarEventDataRequest`/`Response`) is one flat object with every field optional; this is
 * the one place that gets narrowed to what a given `type` actually has.
 */
export type CarEventData =
  | {
      readonly type: 'FUEL'
      /** A decimal string (litres) - no currency, so not a `Money`; see AGENTS.md's garage entry. */
      readonly fuelAmount: string | undefined
      readonly fuelStation: string | undefined
      readonly fuelTypeOverride: FuelType | undefined
    }
  | {
      readonly type: 'REPAIR'
      readonly repairShop: string | undefined
      readonly description: string | undefined
      readonly items: readonly string[]
      readonly fulfilledScheduleIds: readonly string[]
    }
  | {
      readonly type: 'INSURANCE_PAYMENT'
      readonly policyId: string | undefined
      readonly installmentNumber: number | undefined
    }
  | {
      readonly type: 'TAX'
      readonly taxType: string | undefined
      readonly taxPeriod: string | undefined
      readonly authority: string | undefined
      readonly referenceNumber: string | undefined
    }
  | {
      readonly type: 'INSPECTION'
      readonly station: string | undefined
      readonly result: InspectionResult | undefined
      readonly validUntil: LocalDate | undefined
    }
  | {
      readonly type: 'REGISTRATION'
      readonly authority: string | undefined
      readonly expiryDate: LocalDate | undefined
    }
  | { readonly type: 'CAR_WASH'; readonly washType: WashType | undefined }
  | {
      readonly type: 'PARKING'
      readonly location: string | undefined
      readonly durationMinutes: number | undefined
    }
  | {
      readonly type: 'FINE'
      readonly fineType: FineType | undefined
      readonly status: FineStatus | undefined
      readonly authority: string | undefined
      readonly referenceNumber: string | undefined
      readonly dueDate: LocalDate | undefined
    }
  | { readonly type: 'MILEAGE_LOG' }
  | { readonly type: 'SELL'; readonly buyerNotes: string | undefined }
  | { readonly type: 'BUY'; readonly sellerNotes: string | undefined }
  | {
      readonly type: 'TIRE_CHANGE'
      readonly axle: Axle | undefined
      readonly tireType: TireType | undefined
      readonly brand: string | undefined
      readonly shop: string | undefined
    }

export type CarEventJournal =
  | { readonly mode: 'NONE' }
  | {
      readonly mode: 'CREATE'
      readonly expenseAccountId: string
      readonly sourceAccountId: string
      readonly amount: Money
      readonly payee: string | undefined
    }
  | { readonly mode: 'LINK'; readonly entryId: string; readonly expenseAccountId?: string }

export type UpdateCarEventJournal =
  | { readonly mode: 'UNCHANGED' }
  | {
      readonly mode: 'CREATE'
      readonly expenseAccountId: string
      readonly sourceAccountId: string
      readonly amount: Money
      readonly payee: string | undefined
    }
  | { readonly mode: 'LINK'; readonly entryId: string }

export interface CarEvent {
  readonly id: string
  readonly carId: string
  readonly type: CarEventType
  readonly date: LocalDate
  readonly mileageKm: number | undefined
  readonly notes: string | undefined
  readonly data: CarEventData
  readonly journalEntryId: string | undefined
  readonly journalMode: 'CREATED' | 'LINKED' | undefined
  readonly sourceAccountId: string | undefined
  /** The cost the event's journal carries; absent when the event has no journal. */
  readonly amount: Money | undefined
  readonly expenseAccountId: string | undefined
  readonly createdAt: Instant
  readonly updatedAt: Instant
}

export interface RecordCarEventInput {
  readonly type: CarEventType
  readonly date: LocalDate
  readonly data: CarEventData
  readonly journal: CarEventJournal
  readonly mileageKm?: number
  readonly notes?: string
}

export interface UpdateCarEventInput {
  readonly type: CarEventType
  readonly date: LocalDate
  readonly data: CarEventData
  readonly journal: UpdateCarEventJournal
  readonly mileageKm?: number
  readonly notes?: string
}

export type PolicyType = 'THIRD_PARTY' | 'CASCO' | 'COMPREHENSIVE' | 'OTHER'
export type PaymentSchedule = 'ANNUAL' | 'SEMI_ANNUAL' | 'QUARTERLY' | 'MONTHLY'

export interface InsurancePolicy {
  readonly id: string
  readonly carId: string
  readonly insurer: string
  readonly policyNumber: string | undefined
  readonly type: PolicyType
  readonly coverageStart: LocalDate | undefined
  readonly coverageEnd: LocalDate | undefined
  readonly paymentSchedule: PaymentSchedule | undefined
  readonly notes: string | undefined
  readonly createdAt: Instant
  readonly updatedAt: Instant
}

export interface CreateInsurancePolicyInput {
  readonly insurer: string
  readonly type: PolicyType
  readonly policyNumber?: string
  readonly coverageStart?: LocalDate
  readonly coverageEnd?: LocalDate
  readonly paymentSchedule?: PaymentSchedule
  readonly notes?: string
}

export interface UpdateInsurancePolicyInput {
  readonly insurer?: string
  readonly type?: PolicyType
  readonly policyNumber?: string
  readonly coverageStart?: LocalDate
  readonly coverageEnd?: LocalDate
  readonly paymentSchedule?: PaymentSchedule
  readonly notes?: string
}

export interface MaintenanceSchedule {
  readonly id: string
  readonly carId: string
  readonly name: string
  readonly intervalKm: number | undefined
  readonly intervalMonths: number | undefined
  readonly lastDoneDate: LocalDate | undefined
  readonly lastDoneMileageKm: number | undefined
  readonly createdAt: Instant
  readonly updatedAt: Instant
}

export interface CreateMaintenanceScheduleInput {
  readonly name: string
  readonly intervalKm?: number
  readonly intervalMonths?: number
  readonly lastDoneDate?: LocalDate
  readonly lastDoneMileageKm?: number
}

export interface UpdateMaintenanceScheduleInput {
  readonly name?: string
  readonly intervalKm?: number
  readonly intervalMonths?: number
  readonly clearIntervalKm: boolean
  readonly clearIntervalMonths: boolean
}

export interface MarkMaintenanceScheduleDoneInput {
  readonly doneDate: LocalDate
  readonly doneMileageKm?: number
}

export interface CarCostByAccount {
  readonly accountId: string
  readonly amount: Money
}

export interface CarStatisticsPeriod {
  readonly from: LocalDate
  readonly until: LocalDate
}

export interface CarPeriodFigures {
  readonly totalSpend: Money
  readonly spendThisYear: Money
  /** Undefined when the odometer went backwards over the period; zero when it did not move. */
  readonly distanceKm: number | undefined
  /** Undefined whenever there is no distance to divide by - never a figure built on nothing. */
  readonly costPerKm: Money | undefined
}

export interface CarStatistics extends CarPeriodFigures {
  readonly currentMileageKm: number
  readonly breakdown: readonly CarCostByAccount[]
  readonly previousPeriod: { readonly figures: CarPeriodFigures }
}

export interface CarStatisticsQuery {
  readonly displayCurrency: string
  readonly period: CarStatisticsPeriod
}

export const carKeys = {
  list: (ledgerId: string): CacheKey => ledgerKey(ledgerId, 'cars', 'list'),
  detail: (ledgerId: string, carId: string): CacheKey => ledgerKey(ledgerId, 'cars', 'detail', carId),
  events: (ledgerId: string, carId: string): CacheKey => ledgerKey(ledgerId, 'cars', 'events', carId),
  insurancePolicies: (ledgerId: string, carId: string): CacheKey =>
    ledgerKey(ledgerId, 'cars', 'insurance-policies', carId),
  maintenanceSchedules: (ledgerId: string, carId: string): CacheKey =>
    ledgerKey(ledgerId, 'cars', 'maintenance-schedules', carId),
  statistics: (ledgerId: string, carId: string, query: CarStatisticsQuery): CacheKey =>
    ledgerKey(
      ledgerId,
      'cars',
      'statistics',
      carId,
      query.displayCurrency,
      formatLocalDate(query.period.from),
      formatLocalDate(query.period.until),
    ),
  photo: (ledgerId: string, carId: string): CacheKey => ledgerKey(ledgerId, 'cars', 'photo', carId),
  /** Everything a write to a car or anything under it (an event, a policy, a schedule) makes stale - never a photo. */
  invalidatedByWrite: (ledgerId: string): Invalidations => [
    ledgerKey(ledgerId, 'cars', 'list'),
    ledgerKey(ledgerId, 'cars', 'detail'),
    ledgerKey(ledgerId, 'cars', 'events'),
    ledgerKey(ledgerId, 'cars', 'insurance-policies'),
    ledgerKey(ledgerId, 'cars', 'maintenance-schedules'),
    ledgerKey(ledgerId, 'cars', 'statistics'),
  ],
  /** A photo write changes that car's photo and whether the car lists one, on its detail and in the list. */
  invalidatedByPhotoWrite: (ledgerId: string, carId: string): Invalidations => [
    carKeys.photo(ledgerId, carId),
    carKeys.detail(ledgerId, carId),
    carKeys.list(ledgerId),
  ],
} as const

export interface CarsRepository {
  list(ledgerId: string, signal?: AbortSignal): Promise<readonly Car[]>
  get(ledgerId: string, carId: string, signal?: AbortSignal): Promise<Car>
  create(ledgerId: string, input: CreateCarInput): Promise<Car>
  update(ledgerId: string, carId: string, input: UpdateCarInput): Promise<Car>
  delete(ledgerId: string, carId: string): Promise<void>
  setPrimary(ledgerId: string, carId: string): Promise<void>
  statistics(
    ledgerId: string,
    carId: string,
    query: CarStatisticsQuery,
    signal?: AbortSignal,
  ): Promise<CarStatistics>
  /** The stored photo, fetched through the client so the bearer token reaches the server. */
  photo(ledgerId: string, carId: string, signal?: AbortSignal): Promise<BinaryResponse>
  replacePhoto(ledgerId: string, carId: string, file: File): Promise<Car>
  deletePhoto(ledgerId: string, carId: string): Promise<void>

  listEvents(ledgerId: string, carId: string, signal?: AbortSignal): Promise<readonly CarEvent[]>
  getEvent(ledgerId: string, carId: string, eventId: string, signal?: AbortSignal): Promise<CarEvent>
  recordEvent(ledgerId: string, carId: string, input: RecordCarEventInput): Promise<CarEvent>
  updateEvent(ledgerId: string, carId: string, eventId: string, input: UpdateCarEventInput): Promise<CarEvent>
  deleteEvent(ledgerId: string, carId: string, eventId: string): Promise<void>

  listInsurancePolicies(
    ledgerId: string,
    carId: string,
    signal?: AbortSignal,
  ): Promise<readonly InsurancePolicy[]>
  createInsurancePolicy(
    ledgerId: string,
    carId: string,
    input: CreateInsurancePolicyInput,
  ): Promise<InsurancePolicy>
  updateInsurancePolicy(
    ledgerId: string,
    carId: string,
    policyId: string,
    input: UpdateInsurancePolicyInput,
  ): Promise<InsurancePolicy>
  deleteInsurancePolicy(ledgerId: string, carId: string, policyId: string): Promise<void>

  listMaintenanceSchedules(
    ledgerId: string,
    carId: string,
    signal?: AbortSignal,
  ): Promise<readonly MaintenanceSchedule[]>
  createMaintenanceSchedule(
    ledgerId: string,
    carId: string,
    input: CreateMaintenanceScheduleInput,
  ): Promise<MaintenanceSchedule>
  updateMaintenanceSchedule(
    ledgerId: string,
    carId: string,
    scheduleId: string,
    input: UpdateMaintenanceScheduleInput,
  ): Promise<MaintenanceSchedule>
  deleteMaintenanceSchedule(ledgerId: string, carId: string, scheduleId: string): Promise<void>
  markMaintenanceScheduleDone(
    ledgerId: string,
    carId: string,
    scheduleId: string,
    input: MarkMaintenanceScheduleDoneInput,
  ): Promise<MaintenanceSchedule>
}

/** Same type gap as loans.ts's own `apiMoney` - the contract's currency enum is narrower than `@taler/domain`'s. */
function apiMoney(value: Money): Schemas['ApiMoney'] {
  return toApiMoney(value) as Schemas['ApiMoney']
}

export function createCarsRepository(client: TalerClient): CarsRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/cars`
  const one = (ledgerId: string, carId: string) => `${base(ledgerId)}/${encodeURIComponent(carId)}`
  const events = (ledgerId: string, carId: string) => `${one(ledgerId, carId)}/events`
  const oneEvent = (ledgerId: string, carId: string, eventId: string) =>
    `${events(ledgerId, carId)}/${encodeURIComponent(eventId)}`
  const policies = (ledgerId: string, carId: string) => `${one(ledgerId, carId)}/insurance-policies`
  const onePolicy = (ledgerId: string, carId: string, policyId: string) =>
    `${policies(ledgerId, carId)}/${encodeURIComponent(policyId)}`
  const schedules = (ledgerId: string, carId: string) => `${one(ledgerId, carId)}/maintenance-schedules`
  const oneSchedule = (ledgerId: string, carId: string, scheduleId: string) =>
    `${schedules(ledgerId, carId)}/${encodeURIComponent(scheduleId)}`

  return {
    async list(ledgerId, signal) {
      const cars = await client.call<Schemas['CarResponse'][]>({
        method: 'GET',
        path: base(ledgerId),
        ...(signal === undefined ? {} : { signal }),
      })
      return cars.map(toCar)
    },

    async get(ledgerId, carId, signal) {
      return toCar(
        await client.call<Schemas['CarResponse']>({
          method: 'GET',
          path: one(ledgerId, carId),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async create(ledgerId, input) {
      return toCar(
        await client.call<Schemas['CarResponse'], Schemas['CreateCarRequest']>({
          method: 'POST',
          path: base(ledgerId),
          body: apiCreateCar(input),
        }),
      )
    },

    async update(ledgerId, carId, input) {
      return toCar(
        await client.call<Schemas['CarResponse'], Schemas['UpdateCarRequest']>({
          method: 'PATCH',
          path: one(ledgerId, carId),
          body: apiUpdateCar(input),
        }),
      )
    },

    async delete(ledgerId, carId) {
      await client.call<undefined>({ method: 'DELETE', path: one(ledgerId, carId) })
    },

    async setPrimary(ledgerId, carId) {
      await client.call<undefined, Record<string, never>>({
        method: 'POST',
        path: `${one(ledgerId, carId)}/set-primary`,
        body: {},
      })
    },

    async statistics(ledgerId, carId, query, signal) {
      return toCarStatistics(
        await client.call<Schemas['CarStatisticsResponse']>({
          method: 'GET',
          path: `${one(ledgerId, carId)}/statistics`,
          query: {
            displayCurrency: query.displayCurrency,
            from: formatLocalDate(query.period.from),
            until: formatLocalDate(query.period.until),
          },
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async photo(ledgerId, carId, signal) {
      return client.callBinary({
        path: `${one(ledgerId, carId)}/photo`,
        ...(signal === undefined ? {} : { signal }),
      })
    },

    async replacePhoto(ledgerId, carId, file) {
      const path = `${one(ledgerId, carId)}/photo`
      const formData = new FormData()
      formData.append('file', file, file.name)
      const idempotencyPayload = { name: file.name, size: file.size, lastModified: file.lastModified }
      const key = { method: 'PUT', path, query: null, body: idempotencyPayload } as const
      let car: Schemas['CarResponse']
      try {
        car = await client.callMultipart<Schemas['CarResponse']>({
          method: 'PUT',
          path,
          formData,
          idempotencyPayload,
        })
      } catch (error) {
        forgetOnFinalClientError(client.idempotencyKeys, key, error)
        throw error
      }
      client.idempotencyKeys.forget(key)
      return toCar(car)
    },

    async deletePhoto(ledgerId, carId) {
      const path = `${one(ledgerId, carId)}/photo`
      const key = { method: 'DELETE', path, query: null, body: null } as const
      try {
        await client.call<undefined>({ method: 'DELETE', path })
      } catch (error) {
        forgetOnFinalClientError(client.idempotencyKeys, key, error)
        throw error
      }
      client.idempotencyKeys.forget(key)
    },

    async listEvents(ledgerId, carId, signal) {
      const items = await client.call<Schemas['CarEventResponse'][]>({
        method: 'GET',
        path: events(ledgerId, carId),
        ...(signal === undefined ? {} : { signal }),
      })
      return items.map(toCarEvent)
    },

    async getEvent(ledgerId, carId, eventId, signal) {
      return toCarEvent(
        await client.call<Schemas['CarEventResponse']>({
          method: 'GET',
          path: oneEvent(ledgerId, carId, eventId),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async recordEvent(ledgerId, carId, input) {
      return toCarEvent(
        await client.call<Schemas['CarEventResponse'], Schemas['RecordCarEventRequest']>({
          method: 'POST',
          path: events(ledgerId, carId),
          body: {
            type: input.type,
            date: formatLocalDate(input.date),
            data: apiCarEventData(input.data),
            journal: apiCarEventJournal(input.journal),
            // Generation artifact, same as validForCreate/validForLink - see AGENTS.md's garage entry.
            validForSell: true,
            ...(input.mileageKm === undefined ? {} : { mileageKm: input.mileageKm }),
            ...(input.notes === undefined ? {} : { notes: input.notes }),
          },
        }),
      )
    },

    async updateEvent(ledgerId, carId, eventId, input) {
      return toCarEvent(
        await client.call<Schemas['CarEventResponse'], Schemas['UpdateCarEventRequest']>({
          method: 'PATCH',
          path: oneEvent(ledgerId, carId, eventId),
          body: {
            type: input.type,
            date: formatLocalDate(input.date),
            data: apiCarEventData(input.data),
            journal: apiUpdateCarEventJournal(input.journal),
            validForSell: true,
            ...(input.mileageKm === undefined ? {} : { mileageKm: input.mileageKm }),
            ...(input.notes === undefined ? {} : { notes: input.notes }),
          },
        }),
      )
    },

    async deleteEvent(ledgerId, carId, eventId) {
      await client.call<undefined>({ method: 'DELETE', path: oneEvent(ledgerId, carId, eventId) })
    },

    async listInsurancePolicies(ledgerId, carId, signal) {
      const items = await client.call<Schemas['CarInsurancePolicyResponse'][]>({
        method: 'GET',
        path: policies(ledgerId, carId),
        ...(signal === undefined ? {} : { signal }),
      })
      return items.map(toInsurancePolicy)
    },

    async createInsurancePolicy(ledgerId, carId, input) {
      return toInsurancePolicy(
        await client.call<Schemas['CarInsurancePolicyResponse'], Schemas['CreateInsurancePolicyRequest']>({
          method: 'POST',
          path: policies(ledgerId, carId),
          body: apiCreatePolicy(input),
        }),
      )
    },

    async updateInsurancePolicy(ledgerId, carId, policyId, input) {
      return toInsurancePolicy(
        await client.call<Schemas['CarInsurancePolicyResponse'], Schemas['UpdateInsurancePolicyRequest']>({
          method: 'PATCH',
          path: onePolicy(ledgerId, carId, policyId),
          body: apiUpdatePolicy(input),
        }),
      )
    },

    async deleteInsurancePolicy(ledgerId, carId, policyId) {
      await client.call<undefined>({ method: 'DELETE', path: onePolicy(ledgerId, carId, policyId) })
    },

    async listMaintenanceSchedules(ledgerId, carId, signal) {
      const items = await client.call<Schemas['CarMaintenanceScheduleResponse'][]>({
        method: 'GET',
        path: schedules(ledgerId, carId),
        ...(signal === undefined ? {} : { signal }),
      })
      return items.map(toMaintenanceSchedule)
    },

    async createMaintenanceSchedule(ledgerId, carId, input) {
      return toMaintenanceSchedule(
        await client.call<
          Schemas['CarMaintenanceScheduleResponse'],
          Schemas['CreateMaintenanceScheduleRequest']
        >({
          method: 'POST',
          path: schedules(ledgerId, carId),
          body: {
            name: input.name,
            ...(input.intervalKm === undefined ? {} : { intervalKm: input.intervalKm }),
            ...(input.intervalMonths === undefined ? {} : { intervalMonths: input.intervalMonths }),
            ...(input.lastDoneDate === undefined
              ? {}
              : { lastDoneDate: formatLocalDate(input.lastDoneDate) }),
            ...(input.lastDoneMileageKm === undefined ? {} : { lastDoneMileageKm: input.lastDoneMileageKm }),
          },
        }),
      )
    },

    async updateMaintenanceSchedule(ledgerId, carId, scheduleId, input) {
      return toMaintenanceSchedule(
        await client.call<
          Schemas['CarMaintenanceScheduleResponse'],
          Schemas['UpdateMaintenanceScheduleRequest']
        >({
          method: 'PATCH',
          path: oneSchedule(ledgerId, carId, scheduleId),
          body: {
            clearIntervalKm: input.clearIntervalKm,
            clearIntervalMonths: input.clearIntervalMonths,
            ...(input.name === undefined ? {} : { name: input.name }),
            ...(input.intervalKm === undefined ? {} : { intervalKm: input.intervalKm }),
            ...(input.intervalMonths === undefined ? {} : { intervalMonths: input.intervalMonths }),
          },
        }),
      )
    },

    async deleteMaintenanceSchedule(ledgerId, carId, scheduleId) {
      await client.call<undefined>({ method: 'DELETE', path: oneSchedule(ledgerId, carId, scheduleId) })
    },

    async markMaintenanceScheduleDone(ledgerId, carId, scheduleId, input) {
      return toMaintenanceSchedule(
        await client.call<Schemas['CarMaintenanceScheduleResponse'], Schemas['MarkScheduleDoneRequest']>({
          method: 'POST',
          path: `${oneSchedule(ledgerId, carId, scheduleId)}/mark-done`,
          body: {
            doneDate: formatLocalDate(input.doneDate),
            ...(input.doneMileageKm === undefined ? {} : { doneMileageKm: input.doneMileageKm }),
          },
        }),
      )
    },
  }
}

function apiCreateCar(input: CreateCarInput): Schemas['CreateCarRequest'] {
  return {
    name: input.name,
    fuelType: input.fuelType,
    initialMileageKm: input.initialMileageKm,
    isPrimary: input.isPrimary,
    ...(input.make === undefined ? {} : { make: input.make }),
    ...(input.model === undefined ? {} : { model: input.model }),
    ...(input.year === undefined ? {} : { year: input.year }),
    ...(input.licensePlate === undefined ? {} : { licensePlate: input.licensePlate }),
    ...(input.vin === undefined ? {} : { vin: input.vin }),
    ...(input.color === undefined ? {} : { color: input.color }),
    ...(input.purchaseDate === undefined ? {} : { purchaseDate: formatLocalDate(input.purchaseDate) }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  }
}

function apiUpdateCar(input: UpdateCarInput): Schemas['UpdateCarRequest'] {
  return {
    ...(input.name === undefined ? {} : { name: input.name }),
    ...(input.fuelType === undefined ? {} : { fuelType: input.fuelType }),
    ...(input.initialMileageKm === undefined ? {} : { initialMileageKm: input.initialMileageKm }),
    ...(input.make === undefined ? {} : { make: input.make }),
    ...(input.model === undefined ? {} : { model: input.model }),
    ...(input.year === undefined ? {} : { year: input.year }),
    ...(input.licensePlate === undefined ? {} : { licensePlate: input.licensePlate }),
    ...(input.vin === undefined ? {} : { vin: input.vin }),
    ...(input.color === undefined ? {} : { color: input.color }),
    ...(input.purchaseDate === undefined ? {} : { purchaseDate: formatLocalDate(input.purchaseDate) }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  }
}

/** `validForCreate`/`validForLink` carry no documented meaning on the wire - see AGENTS.md's garage entry. */
function apiCarEventJournal(journal: CarEventJournal): Schemas['CarEventJournalRequest'] {
  if (journal.mode === 'NONE') return { mode: 'NONE', validForCreate: true, validForLink: true }
  if (journal.mode === 'LINK') {
    return {
      mode: 'LINK',
      entryId: journal.entryId,
      validForCreate: true,
      validForLink: true,
      ...(journal.expenseAccountId === undefined ? {} : { expenseAccountId: journal.expenseAccountId }),
    }
  }
  return {
    mode: 'CREATE',
    expenseAccountId: journal.expenseAccountId,
    sourceAccountId: journal.sourceAccountId,
    amount: apiMoney(journal.amount),
    validForCreate: true,
    validForLink: true,
    ...(journal.payee === undefined ? {} : { payee: journal.payee }),
  }
}

function apiUpdateCarEventJournal(journal: UpdateCarEventJournal): Schemas['UpdateCarEventJournalRequest'] {
  if (journal.mode === 'UNCHANGED') return { mode: 'UNCHANGED', validForCreate: true, validForLink: true }
  if (journal.mode === 'LINK') {
    return { mode: 'LINK', entryId: journal.entryId, validForCreate: true, validForLink: true }
  }
  return {
    mode: 'CREATE',
    expenseAccountId: journal.expenseAccountId,
    sourceAccountId: journal.sourceAccountId,
    amount: apiMoney(journal.amount),
    validForCreate: true,
    validForLink: true,
    ...(journal.payee === undefined ? {} : { payee: journal.payee }),
  }
}

function apiCarEventData(data: CarEventData): Schemas['CarEventDataRequest'] {
  switch (data.type) {
    case 'FUEL':
      return {
        ...(data.fuelAmount === undefined ? {} : { fuelAmount: data.fuelAmount }),
        ...(data.fuelStation === undefined ? {} : { fuelStation: data.fuelStation }),
        ...(data.fuelTypeOverride === undefined ? {} : { fuelTypeOverride: data.fuelTypeOverride }),
      }
    case 'REPAIR':
      return {
        ...(data.repairShop === undefined ? {} : { repairShop: data.repairShop }),
        ...(data.description === undefined ? {} : { description: data.description }),
        ...(data.items.length === 0 ? {} : { items: [...data.items] }),
        ...(data.fulfilledScheduleIds.length === 0
          ? {}
          : { fulfilledScheduleIds: [...data.fulfilledScheduleIds] }),
      }
    case 'INSURANCE_PAYMENT':
      return {
        ...(data.policyId === undefined ? {} : { policyId: data.policyId }),
        ...(data.installmentNumber === undefined ? {} : { installmentNumber: data.installmentNumber }),
      }
    case 'TAX':
      return {
        ...(data.taxType === undefined ? {} : { taxType: data.taxType }),
        ...(data.taxPeriod === undefined ? {} : { taxPeriod: data.taxPeriod }),
        ...(data.authority === undefined ? {} : { authority: data.authority }),
        ...(data.referenceNumber === undefined ? {} : { referenceNumber: data.referenceNumber }),
      }
    case 'INSPECTION':
      return {
        ...(data.station === undefined ? {} : { station: data.station }),
        ...(data.result === undefined ? {} : { result: data.result }),
        ...(data.validUntil === undefined ? {} : { validUntil: formatLocalDate(data.validUntil) }),
      }
    case 'REGISTRATION':
      return {
        ...(data.authority === undefined ? {} : { authority: data.authority }),
        ...(data.expiryDate === undefined ? {} : { expiryDate: formatLocalDate(data.expiryDate) }),
      }
    case 'CAR_WASH':
      return { ...(data.washType === undefined ? {} : { washType: data.washType }) }
    case 'PARKING':
      return {
        ...(data.location === undefined ? {} : { location: data.location }),
        ...(data.durationMinutes === undefined ? {} : { durationMinutes: data.durationMinutes }),
      }
    case 'FINE':
      return {
        ...(data.fineType === undefined ? {} : { fineType: data.fineType }),
        ...(data.status === undefined ? {} : { status: data.status }),
        ...(data.authority === undefined ? {} : { authority: data.authority }),
        ...(data.referenceNumber === undefined ? {} : { referenceNumber: data.referenceNumber }),
        ...(data.dueDate === undefined ? {} : { dueDate: formatLocalDate(data.dueDate) }),
      }
    case 'MILEAGE_LOG':
      return {}
    case 'SELL':
      return { ...(data.buyerNotes === undefined ? {} : { buyerNotes: data.buyerNotes }) }
    case 'BUY':
      return { ...(data.sellerNotes === undefined ? {} : { sellerNotes: data.sellerNotes }) }
    case 'TIRE_CHANGE':
      return {
        ...(data.axle === undefined ? {} : { axle: data.axle }),
        ...(data.tireType === undefined ? {} : { tireType: data.tireType }),
        ...(data.brand === undefined ? {} : { brand: data.brand }),
        ...(data.shop === undefined ? {} : { shop: data.shop }),
      }
  }
}

function toCarEventData(type: CarEventType, response: Schemas['CarEventDataResponse']): CarEventData {
  switch (type) {
    case 'FUEL':
      return {
        type,
        fuelAmount: response.fuelAmount,
        fuelStation: response.fuelStation,
        fuelTypeOverride: response.fuelTypeOverride,
      }
    case 'REPAIR':
      return {
        type,
        repairShop: response.repairShop,
        description: response.description,
        items: response.items ?? [],
        fulfilledScheduleIds: response.fulfilledScheduleIds ?? [],
      }
    case 'INSURANCE_PAYMENT':
      return { type, policyId: response.policyId, installmentNumber: response.installmentNumber }
    case 'TAX':
      return {
        type,
        taxType: response.taxType,
        taxPeriod: response.taxPeriod,
        authority: response.authority,
        referenceNumber: response.referenceNumber,
      }
    case 'INSPECTION':
      return {
        type,
        station: response.station,
        result: response.result,
        validUntil: response.validUntil === undefined ? undefined : parseLocalDate(response.validUntil),
      }
    case 'REGISTRATION':
      return {
        type,
        authority: response.authority,
        expiryDate: response.expiryDate === undefined ? undefined : parseLocalDate(response.expiryDate),
      }
    case 'CAR_WASH':
      return { type, washType: response.washType }
    case 'PARKING':
      return { type, location: response.location, durationMinutes: response.durationMinutes }
    case 'FINE':
      return {
        type,
        fineType: response.fineType,
        status: response.status,
        authority: response.authority,
        referenceNumber: response.referenceNumber,
        dueDate: response.dueDate === undefined ? undefined : parseLocalDate(response.dueDate),
      }
    case 'MILEAGE_LOG':
      return { type }
    case 'SELL':
      return { type, buyerNotes: response.buyerNotes }
    case 'BUY':
      return { type, sellerNotes: response.sellerNotes }
    case 'TIRE_CHANGE':
      return {
        type,
        axle: response.axle,
        tireType: response.tireType,
        brand: response.brand,
        shop: response.shop,
      }
  }
}

function apiCreatePolicy(input: CreateInsurancePolicyInput): Schemas['CreateInsurancePolicyRequest'] {
  return {
    insurer: input.insurer,
    type: input.type,
    ...(input.policyNumber === undefined ? {} : { policyNumber: input.policyNumber }),
    ...(input.coverageStart === undefined ? {} : { coverageStart: formatLocalDate(input.coverageStart) }),
    ...(input.coverageEnd === undefined ? {} : { coverageEnd: formatLocalDate(input.coverageEnd) }),
    ...(input.paymentSchedule === undefined ? {} : { paymentSchedule: input.paymentSchedule }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  }
}

function apiUpdatePolicy(input: UpdateInsurancePolicyInput): Schemas['UpdateInsurancePolicyRequest'] {
  return {
    ...(input.insurer === undefined ? {} : { insurer: input.insurer }),
    ...(input.type === undefined ? {} : { type: input.type }),
    ...(input.policyNumber === undefined ? {} : { policyNumber: input.policyNumber }),
    ...(input.coverageStart === undefined ? {} : { coverageStart: formatLocalDate(input.coverageStart) }),
    ...(input.coverageEnd === undefined ? {} : { coverageEnd: formatLocalDate(input.coverageEnd) }),
    ...(input.paymentSchedule === undefined ? {} : { paymentSchedule: input.paymentSchedule }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
  }
}

function toCar(response: Schemas['CarResponse']): Car {
  return {
    id: response.id,
    name: response.name,
    make: response.make,
    model: response.model,
    year: response.year,
    licensePlate: response.licensePlate,
    vin: response.vin,
    color: response.color,
    fuelType: response.fuelType,
    initialMileageKm: response.initialMileageKm,
    purchaseDate: response.purchaseDate === undefined ? undefined : parseLocalDate(response.purchaseDate),
    notes: response.notes,
    status: response.status,
    isPrimary: response.isPrimary,
    hasPhoto: response.hasPhoto,
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
  }
}

function toCarEvent(response: Schemas['CarEventResponse']): CarEvent {
  return {
    id: response.id,
    carId: response.carId,
    type: response.type,
    date: parseLocalDate(response.date),
    mileageKm: response.mileageKm,
    notes: response.notes,
    data: toCarEventData(response.type, response.data),
    journalEntryId: response.journalEntryId,
    journalMode: response.journalMode,
    sourceAccountId: response.sourceAccountId,
    amount: response.amount === undefined ? undefined : fromApiMoney(response.amount),
    expenseAccountId: response.expenseAccountId,
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
  }
}

function toInsurancePolicy(response: Schemas['CarInsurancePolicyResponse']): InsurancePolicy {
  return {
    id: response.id,
    carId: response.carId,
    insurer: response.insurer,
    policyNumber: response.policyNumber,
    type: response.type,
    coverageStart: response.coverageStart === undefined ? undefined : parseLocalDate(response.coverageStart),
    coverageEnd: response.coverageEnd === undefined ? undefined : parseLocalDate(response.coverageEnd),
    paymentSchedule: response.paymentSchedule,
    notes: response.notes,
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
  }
}

function toMaintenanceSchedule(response: Schemas['CarMaintenanceScheduleResponse']): MaintenanceSchedule {
  return {
    id: response.id,
    carId: response.carId,
    name: response.name,
    intervalKm: response.intervalKm,
    intervalMonths: response.intervalMonths,
    lastDoneDate: response.lastDoneDate === undefined ? undefined : parseLocalDate(response.lastDoneDate),
    lastDoneMileageKm: response.lastDoneMileageKm,
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
  }
}

type CarFiguresWire = Pick<
  Schemas['CarPeriodFiguresResponse'],
  'totalSpend' | 'spendThisYear' | 'distanceKm' | 'costPerKm'
>

function toCarPeriodFigures(response: CarFiguresWire): CarPeriodFigures {
  return {
    totalSpend: fromApiMoney(response.totalSpend),
    spendThisYear: fromApiMoney(response.spendThisYear),
    distanceKm: response.distanceKm,
    costPerKm: response.costPerKm === undefined ? undefined : fromApiMoney(response.costPerKm),
  }
}

function toCarStatistics(response: Schemas['CarStatisticsResponse']): CarStatistics {
  return {
    ...toCarPeriodFigures(response),
    currentMileageKm: response.currentMileageKm,
    breakdown: response.breakdown.map((entry) => ({
      accountId: entry.accountId,
      amount: fromApiMoney(entry.amount),
    })),
    previousPeriod: {
      figures: toCarPeriodFigures(response.previousPeriod.figures),
    },
  }
}
